import { Link } from "react-router-dom";
import { LogoMark } from "@/components/Logo";
import { SERVICE_AREA, company, identityLine, isSet } from "@/lib/company";

/**
 * The footer on every public page.
 *
 * One component rather than markup repeated per page, because what belongs in
 * it is a legal question, not a layout one: the registered entity has to be
 * identifiable and the policies have to be reachable from anywhere a customer
 * can transact. A page that quietly lacks the terms link is the page somebody
 * books from.
 *
 * Every field comes from lib/company and is omitted when unsupplied, so this
 * never prints a placeholder where a registration number should be.
 */
export function SiteFooter() {
  const year = new Date().getFullYear();

  return (
    <footer className="mt-16 border-t border-ink-200 bg-white">
      <div className="mx-auto max-w-6xl px-4 py-10 lg:px-8">
        <div className="flex flex-wrap items-start justify-between gap-8">
          <div>
            <div className="flex items-center gap-2 font-bold tracking-tight">
              <LogoMark className="h-7 w-7" />
              {company.tradingName}
            </div>
            <p className="mt-3 max-w-xs text-sm text-ink-500">
              Courier services across {SERVICE_AREA}, with prices worked out from the real distance
              your parcel travels.
            </p>
          </div>

          <nav className="grid gap-x-10 gap-y-2 text-sm text-ink-600 sm:grid-cols-2">
            <Link to="/track" className="hover:text-brand">
              Track a parcel
            </Link>
            <Link to="/terms" className="hover:text-brand">
              Terms of service
            </Link>
            <Link to="/ship" className="hover:text-brand">
              Send without an account
            </Link>
            <Link to="/privacy" className="hover:text-brand">
              Privacy policy
            </Link>
            <Link to="/login" className="hover:text-brand">
              Sign in
            </Link>
            <Link to="/refunds" className="hover:text-brand">
              Refunds &amp; cancellations
            </Link>
            <Link to="/register" className="hover:text-brand">
              Create account
            </Link>
            <Link to="/prohibited-items" className="hover:text-brand">
              What we cannot carry
            </Link>
          </nav>

          <div className="text-sm text-ink-600">
            <a className="block hover:text-brand" href={`mailto:${company.supportEmail}`}>
              {company.supportEmail}
            </a>
            {isSet(company.phone) && (
              <a className="mt-1 block hover:text-brand" href={`tel:${company.phone.replace(/\s/g, "")}`}>
                {company.phone}
              </a>
            )}
            {isSet(company.streetAddress) && <p className="mt-3 max-w-xs text-ink-500">{company.streetAddress}</p>}
            {isSet(company.operatingHours) && <p className="mt-1 text-ink-500">{company.operatingHours}</p>}
          </div>
        </div>

        <div className="mt-8 border-t border-ink-100 pt-6 text-xs text-ink-400">
          <p>{identityLine()}</p>
          <p className="mt-1">
            &copy; {year} {company.tradingName}. Liability for loss or damage is limited — see the{" "}
            <Link className="text-brand" to="/terms">terms of service</Link>.
          </p>
        </div>
      </div>
    </footer>
  );
}
